/**
 * What fits on the Wall, and a "+N more" for the rest. A TV has no scroll bar, so every list that can
 * run out of room shows whole rows only and says how many it left out; nothing is dropped silently,
 * and nothing is drawn over something else.
 *
 * The arithmetic is pure, so it can be tested without a layout engine. `useFitRows` and
 * `useFitTiles` do the measuring: each first draws everything (a list also draws one "+N more" row to
 * measure), reads the sizes in a layout effect, before the browser paints, and draws again with what
 * fits. They measure again whenever what they are given changes, and once the web fonts have loaded,
 * since those change every size.
 */
import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * How many of `heights` to show in `room` px, with `gap` px between rows. When some must be left out
 * — they do not all fit, or `total` says there are more than were drawn — the rows shown leave room
 * for a "+N more" row of `moreHeight` px after them.
 */
export function fitRows(heights: number[], room: number, gap: number, moreHeight: number, total = heights.length): number {
  const sum = (k: number) => heights.slice(0, k).reduce((a, h) => a + h, 0) + gap * Math.max(0, k - 1);
  if (total <= heights.length && sum(heights.length) <= room) return heights.length;
  for (let k = heights.length; k > 0; k--) if (sum(k) + gap + moreHeight <= room) return k;
  return 0;
}

export interface TileGroup {
  /** Tiles in the group. */
  count: number;
  /** The group's heading, which a group is never narrower than. */
  headerWidth: number;
}

export interface TileMetrics {
  tileWidth: number;
  tileGap: number;
  /** Between one group and the next: the band's gap, and the next group's padding and rule. */
  groupGap: number;
}

/**
 * The Available now band: one row of groups, each a row of whole tiles. Returns how many tiles each
 * group shows (the same cap for every group, as large as fits), and how many groups are shown at all.
 * A group cut short spends one tile's width on its "+N more"; when even a "+N more" for every group
 * does not fit, groups are left off the end and the band ends with a "+N more" of its own, which
 * `groups` < the number of groups says.
 */
export function fitTiles(groups: TileGroup[], room: number, m: TileMetrics): { cap: number; groups: number } {
  const tilesWidth = (n: number) => (n > 0 ? n * m.tileWidth + (n - 1) * m.tileGap : 0);
  const width = (gs: TileGroup[], cap: number, trailingMore: boolean) =>
    gs.reduce((w, g, i) => w + (i ? m.groupGap : 0) + Math.max(g.headerWidth, tilesWidth(Math.min(g.count, cap) + (g.count > cap ? 1 : 0))), 0) +
    (trailingMore ? m.groupGap + m.tileWidth : 0);
  const most = Math.max(0, ...groups.map((g) => g.count));
  for (let cap = most; cap >= 0; cap--) if (width(groups, cap, false) <= room) return { cap, groups: groups.length };
  for (let n = groups.length - 1; n > 0; n--) if (width(groups.slice(0, n), 0, true) <= room) return { cap: 0, groups: n };
  return { cap: 0, groups: 0 };
}

/** Bumps once the page's web fonts have loaded, since every measured size changes with them. */
export function useFontsLoaded(): number {
  const [loaded, setLoaded] = useState(0);
  useEffect(() => {
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (!fonts) return;
    let live = true;
    const bump = () => {
      if (live) setLoaded((n) => n + 1);
    };
    void fonts.ready.then(bump);
    fonts.addEventListener?.('loadingdone', bump);
    return () => {
      live = false;
      fonts.removeEventListener?.('loadingdone', bump);
    };
  }, []);
  return loaded;
}

const same = (a: readonly unknown[], b: readonly unknown[]) => a.length === b.length && a.every((x, i) => Object.is(x, b[i]));
const px = (v: string) => parseFloat(v) || 0;

/**
 * Measures a list and says how many of its rows to show. While `measuring`, draw the first `drawable`
 * rows and, last, a "+N more" row marked `data-probe`; after it, draw the first `shown` and, when that
 * is fewer than `total`, a "+N more" row. `deps` are what the rows are drawn from.
 */
export function useFitRows<T extends HTMLElement>(
  drawable: number,
  deps: readonly unknown[],
  total = drawable,
): { ref: RefObject<T | null>; shown: number; measuring: boolean } {
  const ref = useRef<T | null>(null);
  const [fit, setFit] = useState<{ deps: readonly unknown[]; shown: number } | null>(null);
  const measuring = !fit || !same(fit.deps, deps);
  useLayoutEffect(() => {
    if (!measuring || !ref.current) return;
    const el = ref.current;
    const kids = [...el.children] as HTMLElement[];
    const probe = kids.find((k) => k.dataset.probe !== undefined);
    const rows = kids.filter((k) => k !== probe);
    const cs = getComputedStyle(el);
    const room = el.clientHeight - px(cs.paddingTop) - px(cs.paddingBottom);
    setFit({ deps, shown: fitRows(rows.map((r) => r.offsetHeight), room, px(cs.rowGap), probe?.offsetHeight ?? 0, total) });
  });
  return { ref, shown: measuring ? drawable : Math.min(fit.shown, drawable), measuring };
}

/**
 * Measures the Available now band (`.stock`, holding `.st-title` and one `.grp` per group) and says
 * how many tiles each group shows and how many groups are shown. While `measuring`, draw every tile.
 */
export function useFitTiles<T extends HTMLElement>(
  counts: number[],
  deps: readonly unknown[],
): { ref: RefObject<T | null>; cap: number; groups: number; measuring: boolean } {
  const ref = useRef<T | null>(null);
  const [fit, setFit] = useState<{ deps: readonly unknown[]; cap: number; groups: number } | null>(null);
  const measuring = !fit || !same(fit.deps, deps);
  useLayoutEffect(() => {
    if (!measuring || !ref.current) return;
    const band = ref.current;
    const cs = getComputedStyle(band);
    const bandGap = px(cs.columnGap);
    const title = band.querySelector<HTMLElement>('.st-title');
    const grps = [...band.querySelectorAll<HTMLElement>('.grp')];
    const tiles = band.querySelector<HTMLElement>('.tiles');
    const second = grps[1] ? getComputedStyle(grps[1]) : null;
    const room = band.clientWidth - px(cs.paddingLeft) - px(cs.paddingRight) - (title ? title.offsetWidth + bandGap : 0);
    const metrics = {
      tileWidth: band.querySelector<HTMLElement>('.tile')?.offsetWidth ?? 0,
      tileGap: tiles ? px(getComputedStyle(tiles).columnGap) : 0,
      groupGap: bandGap + (second ? px(second.paddingLeft) + px(second.borderLeftWidth) : 0),
    };
    const headerWidths = grps.map((g) => g.querySelector<HTMLElement>('.g-h')?.offsetWidth ?? 0);
    setFit({ deps, ...fitTiles(counts.map((count, i) => ({ count, headerWidth: headerWidths[i] ?? 0 })), room, metrics) });
  });
  return { ref, cap: measuring ? Infinity : fit.cap, groups: measuring ? counts.length : fit.groups, measuring };
}
